import { Input } from '../../../../ui/input';
import { Label } from '../../../../ui/label';
import type { AddressFieldSchema, AddressValue, BaseFieldProps } from '../../../types';

export function AddressFieldInput({
  field,
  value,
  onChange,
  error,
}: BaseFieldProps<AddressFieldSchema, AddressValue | null | undefined>) {
  const address = value || {};

  const handleChange = (key: keyof AddressValue, val: string) => {
    onChange?.({
      ...address,
      [key]: val,
    });
  };

  return (
    <div className="grid gap-3">
      <div className="grid gap-1.5">
        <Label
          htmlFor={`${field.id}-street`}
          className="text-[10px] uppercase text-muted-foreground font-semibold"
        >
          Street Address
        </Label>
        <Input
          id={`${field.id}-street`}
          placeholder="123 Main St"
          value={address.street || ''}
          onChange={(e) => handleChange('street', e.target.value)}
        />
      </div>

      <div className="grid grid-cols-2 gap-3">
        <div className="grid gap-1.5">
          <Label
            htmlFor={`${field.id}-city`}
            className="text-[10px] uppercase text-muted-foreground font-semibold"
          >
            City
          </Label>
          <Input
            id={`${field.id}-city`}
            placeholder="San Francisco"
            value={address.city || ''}
            onChange={(e) => handleChange('city', e.target.value)}
          />
        </div>
        <div className="grid gap-1.5">
          <Label
            htmlFor={`${field.id}-state`}
            className="text-[10px] uppercase text-muted-foreground font-semibold"
          >
            State / Province
          </Label>
          <Input
            id={`${field.id}-state`}
            placeholder="CA"
            value={address.state || ''}
            onChange={(e) => handleChange('state', e.target.value)}
          />
        </div>
      </div>

      <div className={field.includeCountry ? 'grid grid-cols-2 gap-3' : 'grid gap-1.5'}>
        <div className="grid gap-1.5">
          <Label
            htmlFor={`${field.id}-zip`}
            className="text-[10px] uppercase text-muted-foreground font-semibold"
          >
            ZIP / Postal Code
          </Label>
          <Input
            id={`${field.id}-zip`}
            placeholder="94105"
            value={address.zip || ''}
            onChange={(e) => handleChange('zip', e.target.value)}
          />
        </div>
        {field.includeCountry && (
          <div className="grid gap-1.5">
            <Label
              htmlFor={`${field.id}-country`}
              className="text-[10px] uppercase text-muted-foreground font-semibold"
            >
              Country
            </Label>
            <Input
              id={`${field.id}-country`}
              placeholder="United States"
              value={address.country || ''}
              onChange={(e) => handleChange('country', e.target.value)}
            />
          </div>
        )}
      </div>
      {error && <p className="text-[10px] text-destructive mt-1">{error}</p>}
    </div>
  );
}

export function AddressFieldView({
  field,
  value,
  viewPlaceholder,
}: BaseFieldProps<AddressFieldSchema, AddressValue | null | undefined>) {
  if (!value || (!value.street && !value.city && !value.state && !value.zip)) {
    return <span className="text-muted-foreground text-sm">{viewPlaceholder || '- -'}</span>;
  }

  return (
    <div className="flex flex-col text-sm">
      {value.street && <span className="font-medium">{value.street}</span>}
      <span className="text-muted-foreground">
        {[value.city, value.state, value.zip].filter(Boolean).join(', ')}
      </span>
      {field.includeCountry && value.country && (
        <span className="text-muted-foreground">{value.country}</span>
      )}
    </div>
  );
}
